import { PropsWithChildren, createContext, useState } from 'react'
import {
  ContextProps,
  FormatPond,
  Init,
  InitialDataFreshWater,
  Option,
  Pisciculture,
  State,
  UvFilter,
} from './types'
import { useStorage } from '../../hooks/useStorage'

export const FreshWaterContext = createContext({} as ContextProps)

export const FreshWaterProvider = ({ children }: PropsWithChildren) => {
  const [initialData, setInitialData] = useState<Init>({
    hasData: false,
    mortalityTypes: [],
    piscicultures: [],
    states: [],
    rooms: [],
    ponds: [],
    uvFilters: {},
    uvStates: [],
  })

  const { isFirstRender, removeStorage } = useStorage({
    initialData,
    setDataFromStorage: (data) => setInitialData(data),
    key: 'aqua-data-freshWater',
  })
  const handleInittialData = (data: InitialDataFreshWater) => {
    isFirstRender.current = false
    setInitialData({
      ...data,
      piscicultures: formatPisciculturesData(data.piscicultures),
      rooms: formatRoomsData(data.piscicultures),
      ponds: formatPondsData(data.piscicultures),
      states: formatStatesData(data.states),
      uvStates: formatStatesData(data.states_uv),
      uvFilters: formarUvFilterstData(data.piscicultures),
      hasData: true,
    })
  }

  const formatPisciculturesData = (data: Pisciculture[]) =>
    data.map((item) => ({
      name: item.name,
      id: item.piscicultureId,
    }))

  const formatRoomsData = (piscicultures: Pisciculture[]): { [key: number]: Option[] } =>
    Object.fromEntries(
      piscicultures.map(({ piscicultureId, rooms }) => [
        piscicultureId,
        rooms.map((room) => ({ name: room.name, id: room.roomId })),
      ]),
    )

  const formatPondsData = (piscicultures: Pisciculture[]): { [key: number]: FormatPond[] } =>
    Object.fromEntries(
      piscicultures.flatMap(({ rooms }) =>
        rooms.map(({ roomId, tanks }) => [
          roomId,
          tanks.map((tank) => ({ ...tank, id: tank.tankId })),
        ]),
      ),
    )

  const formarUvFilterstData = (piscicultures: Pisciculture[]): { [key: number]: UvFilter[] } =>
    Object.fromEntries(
      piscicultures.flatMap(({ rooms }) =>
        rooms.map(({ roomId, uvfilters }) => [
          roomId,
          uvfilters.map((uv) => ({ ...uv, id: uv.filterId })),
        ]),
      ),
    )

  const formatStatesData = (data: State[]): Option[] => [
    ...data.map((state) => ({
      id: state.stateId,
      name: state.name,
    })),
    { id: 0, name: 'Seleccione' },
  ]

  const handleResetData = async () => {
    await removeStorage()
    setInitialData({
      hasData: false,
      mortalityTypes: [],
      piscicultures: [],
      states: [],
      rooms: [],
      ponds: [],
      uvFilters: {},
      uvStates: [],
    })
  }

  return (
    <FreshWaterContext.Provider
      value={{
        mortalityTypes: initialData.mortalityTypes,
        piscicultures: initialData.piscicultures,
        states: initialData.states,
        rooms: initialData.rooms,
        ponds: initialData.ponds,
        handleInittialData,
        hasData: initialData.hasData,
        handleResetData,
        uvStates: initialData.uvStates,
        uvFilters: initialData.uvFilters,
      }}
    >
      {children}
    </FreshWaterContext.Provider>
  )
}
